// This class implements a Lizard Raster's "style" to be used in WMS requests and legends.

import { Raster } from "../endpoints/rasters";

export class RasterStyle {
  private raster: Raster;
  private style: string;
  private canRestyle: boolean;
  private min: number | null;
  private max: number | null;
  private editedStyle: string | null;
  private editedMin: number | null;
  private editedMax: number | null;

  constructor(raster: Raster, min?: number | null, max?: number | null, style?: string | null) {
    this.raster = raster;

    const { options } = raster;
    let styles = (options ? options["styles"] : "") ?? "";

    if (styles && typeof styles !== "string") {
      styles = styles["0"]["0"];
    }
    const partsFromStyle = this.getPartsFromStyle(styles);

    this.canRestyle = partsFromStyle[0] !== null;
    if (partsFromStyle[0] !== null) {
      [this.style, this.min, this.max] = partsFromStyle;
      this.editedStyle = style ?? null;
      this.editedMin = min ?? null;
      this.editedMax = max ?? null;
    } else {
      this.style = styles;
      this.min = null;
      this.max = null;
      this.editedStyle = null;
      this.editedMin = null;
      this.editedMax = null;
    }
  }

  private getPartsFromStyle(style: string): [null, null, null] | [string, number, number] {
    const parts = style.split(":");
    if (parts.length !== 3) {
      return [null, null, null];
    }
    const min = parseFloat(parts[1]);
    const max = parseFloat(parts[2]);

    if (isNaN(min) || isNaN(max)) {
      return [null, null, null];
    }

    return [parts[0], min, max];
  }

  getStyle() {
    return this.canRestyle ? this.editedStyle ?? this.style : this.style;
  }

  getMin() {
    return this.canRestyle ? this.editedMin ?? this.min : null;
  }

  getMax() {
    return this.canRestyle ? this.editedMax ?? this.max : null;
  }

  getCanRestyle() {
    return this.canRestyle;
  }

  setMin(min: number | null) {
    if (min !== null && min !== this.editedMin) {
      return new RasterStyle(this.raster, min, this.editedMax ?? null, this.editedStyle ?? null);
    } else {
      return this;
    }
  }

  setMax(max: number | null) {
    if (max !== null && max !== this.editedMax) {
      return new RasterStyle(this.raster, this.editedMin ?? null, max, this.editedStyle ?? null);
    } else {
      return this;
    }
  }

  resetMin() {
    return new RasterStyle(this.raster, null, this.editedMax, this.editedStyle);
  }

  resetMax() {
    return new RasterStyle(this.raster, this.editedMin, null, this.editedStyle);
  }

  resetStyle() {
    return new RasterStyle(this.raster, this.editedMin, this.editedMax, null);
  }

  toString(): string {
    return this.canRestyle ? `${this.getStyle()}:${this.getMin()}:${this.getMax()}` : this.style;
  }
}
